import { Component, OnInit, OnDestroy, ViewChild, ViewContainerRef, ComponentRef, ComponentFactoryResolver, Input } from '@angular/core';
import { IModalDialog, IModalDialogButton, ModalDialogOnAction, IModalDialogOptions, IModalDialogSettings } from './achoo-modal-dialog.interface';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { delay } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

@Component({
  selector: 'achoo-modal-dialog',
  templateUrl: './achoo-modal-dialog.component.html',
  styleUrls: ['./achoo-modal-dialog.component.scss']
})
export class AchooModalDialogComponent implements IModalDialog, OnDestroy, OnInit {
  @ViewChild('modalDialogBody', { read: ViewContainerRef })
  public dynamicComponentTarget: ViewContainerRef;
  public reference: ComponentRef<IModalDialog>;

  /** Modal dialog style settings */
  public settings: IModalDialogSettings = {
    overlayClass: 'modal-backdrop fade',
    overlayAnimationTriggerClass: 'show',
    modalClass: 'modal ngx-modal fade',
    modalAnimationTriggerClass: 'show',
    modalDialogClass: 'modal-dialog modal-dialog-centered',
    contentClass: 'modal-content',
    headerClass: 'modal-header',
    headerTitleClass: 'modal-title',
    closeButtonClass: 'close glyphicon glyphicon-remove',
    closeButtonTitle: 'CLOSE',
    bodyClass: 'modal-body',
    footerClass: 'modal-footer',
    alertClass: 'ngx-modal-shake',
    alertDuration: 250,
    notifyWithAlert: true,
    buttonClass: 'btn btn-primary'
  };
  @Input() public actionButtons: IModalDialogButton[];
  @Input() public title: string;
  @Input() public onClose: () => Promise<any> | Observable<any> | boolean;

  public showAlert = false;
  public animateOverlayClass = '';
  public animateModalClass = '';

  private _inProgress = false;
  private _alertTimeout: number;
  private _childInstance: any;
  private _closeDialog$: Subject<void>;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver
  ) { }

  /**
   * Initialize dialog with reference to instance and options
   * @param reference
   * @param options
   */
  dialogInit(reference: ComponentRef<IModalDialog>, options: Partial<IModalDialogOptions<any>> = {}) {
    this.reference = reference;

    // inject component
    if (options.childComponent) {
      const factory = this.componentFactoryResolver.resolveComponentFactory(options.childComponent);
      const componentRef = this.dynamicComponentTarget.createComponent(factory) as ComponentRef<IModalDialog>;
      this._childInstance = componentRef.instance as IModalDialog;

      this._closeDialog$ = new Subject<void>();
      this._closeDialog$.subscribe(() => {
        this._finalizeAndDestroy();
      });

      options.closeDialogSubject = this._closeDialog$;

      this._childInstance['dialogInit'](componentRef, options);
      document.activeElement != null ?
        (document.activeElement as HTMLElement).blur() :
        (document.body as HTMLElement).blur();
    }
    // set options
    this._setOptions(options);
  }

  ngOnInit() {
    // a trick to defer css animations
    of(void 0).pipe(delay(0)).subscribe(() => {
      this.animateOverlayClass = this.settings.overlayAnimationTriggerClass;
      this.animateModalClass = this.settings.modalAnimationTriggerClass;
    });
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy() {
    // run animations
    this.animateOverlayClass = '';
    this.animateModalClass = '';

    // cleanup listeners
    if (this._alertTimeout) {
      clearTimeout(this._alertTimeout);
      this._alertTimeout = null;
    }

    if (this._closeDialog$) {
      this._closeDialog$.unsubscribe();
    }
  }

  /**
   * Run action defined on action button
   * @param action
   */
  doAction(action?: ModalDialogOnAction) {
    // disable multi clicks
    if (this._inProgress) {
      return;
    }
    this._inProgress = true;
    this._closeIfSuccessful(action);
  }

  /**
   * Method to run on close
   * if action buttons are defined, it will not close
   */
  close() {
    if (this._inProgress) {
      return;
    }
    if (this.actionButtons && this.actionButtons.length) {
      return;
    }
    this._inProgress = true;

    if (this.onClose) {
      this._closeIfSuccessful(this.onClose);
      return;
    }
    this._finalizeAndDestroy();
  }

  /**
   * Pass options from dialog setup to component
   * @param  {IModalDialogOptions} options?
   */
  private _setOptions(options: Partial<IModalDialogOptions<any>>) {

    if (options.onClose && options.actionButtons && options.actionButtons.length) {
      throw new Error(`OnClose callback and ActionButtons are not allowed to be defined on the same dialog.`);
    }
    // set references
    this.title = (options && options.title) || '';
    this.onClose = (options && options.onClose) || null;
    this.actionButtons = (this._childInstance && this._childInstance['actionButtons']) ||
      (options && options.actionButtons) || null;
    if (options && options.settings) {
      Object.assign(this.settings, options.settings);
    }
  }

  /**
   * Close if successful
   * @param callback
   */
  private _closeIfSuccessful(callback: ModalDialogOnAction) {
    if (!callback) {
      return this._finalizeAndDestroy();
    }

    let response = callback();
    if (typeof response === 'boolean') {
      if (response) {
        return this._finalizeAndDestroy();
      }
      return this._triggerAlert();
    }
    if (response instanceof Promise) {
      response = Observable.from(<Promise<any>>response);
    }
    if (response instanceof Observable) {
      response.subscribe(() => {
        this._finalizeAndDestroy();
      }, () => {
        this._triggerAlert();
      });
    } else {
      this._inProgress = false;
    }
  }

  private _finalizeAndDestroy() {
    this._inProgress = false;
    this.reference.destroy();
  }

  private _triggerAlert() {
    if (this.settings.notifyWithAlert) {
      this.showAlert = true;
      this._alertTimeout = window.setTimeout(() => {
        this.showAlert = false;
        this._inProgress = false;
        clearTimeout(this._alertTimeout);
        this._alertTimeout = null;
      }, this.settings.alertDuration);
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/core/achoo-modal-dialog/achoo-modal-dialog.component.ts